import useHttpProvider from "@/common/hooks/use-http";
import SEO from "@/common/components/seo";
import { Button } from "@/components/ui/button";
import { formatShortNameWithState } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Winner } from "../models/winner";
import * as WinnersService from "../services/winners";

function Header() {
	return (
		<div
			className="flex h-screen flex-col items-center justify-center"
			style={{
				backgroundImage: "url(/images/header-background.png)",
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}}
		>
			<p className="web-heading-h1 z-3 text-center text-white max-sm:p-4">
				Previous Winners
			</p>
			<p className="web-body-b1 mt-4 max-w-[60ch] px-6 text-center text-white-80">
				Celebrating our lucky legends. Your name could be next.
			</p>
		</div>
	);
}

function PreviousWinner({ previousWinner }: { previousWinner: Winner }) {
	const baseURL = import.meta.env.VITE_SERVER_API_URL;
	const prizeImage = `${baseURL}/common/image/${previousWinner.promotion?.prize?.images[0].url}`;
	const winnerPhoto = previousWinner.photo
		? `${baseURL}/common/image/${previousWinner.photo}`
		: prizeImage;

	return (
		<div className="flex flex-col gap-4 text-left md:gap-8">
			<div
				className="aspect-[4/3] w-full rounded-[32px] bg-cover bg-center bg-no-repeat"
				style={{
					backgroundImage: `url('${winnerPhoto}')`,
				}}
			></div>

			<div className="flex flex-col gap-2">
				<div className="flex flex-col justify-between md:flex-row">
					<p className="web-heading-h4">
						{formatShortNameWithState(
							previousWinner.winner.fullName,
							previousWinner.winner.stateName
						)}
					</p>
					<p className="web-body-b1 text-gray-500">
						{previousWinner.promotion.endDate.toString()}
					</p>
				</div>
				<span className="web-body-b1 text-gray-700">
					{previousWinner.promotion.prize.title}
				</span>
			</div>
		</div>
	);
}

function PreviousWinners() {
	const { http } = useHttpProvider();

	const [previousWinners, setPreviousWinners] = useState<Winner[]>([]);
	const [visibleCount, setVisibleCount] = useState(6);

	const visiblePrizes = previousWinners.slice(0, visibleCount);

	const getPreviousWinners = async () => {
		try {
			const winners = await WinnersService.getPreviousWinners(http);
			setPreviousWinners(winners);
		} catch {}
	};

	const handleLoadMore = () => {
		setVisibleCount((prev) => prev + 6);
	};

	useEffect(() => {
		getPreviousWinners();
	}, []);

	return (
		<div className="flex flex-col justify-center gap-16 p-4 py-16 md:gap-12 md:px-37 md:py-50">
			<div className="relative flex flex-col gap-4 text-center">
				<span className="web-heading-h2 md:text-center">
					<span
						style={{
							background:
								"linear-gradient(161deg, #0FF1E5 3.9%, #0DAAEC 33.24%, #0823F8 72.3%, #000D74 116.02%)",
							WebkitBackgroundClip: "text",
							backgroundClip: "text",
							WebkitTextFillColor: "transparent",
						}}
					>
						Lucky
					</span>{" "}
					Legends
				</span>
				<p className="web-body-b1">
					These lucky anglers reeled in the best rewards, will you be next?!
				</p>

				<img
					src="icons/trophy.png"
					className="absolute -top-35 right-3 h-15 w-15 md:-top-20 md:-right-15 md:h-40 md:w-36"
				/>

				<img
					src="icons/star.png"
					className="absolute -top-15 right-30 h-6 w-6 md:top-20 md:right-40 md:h-13 md:w-13"
				/>
			</div>

			<div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-8">
				{visiblePrizes.map((previousWinner) => (
					<PreviousWinner
						key={previousWinner.id}
						previousWinner={previousWinner}
					/>
				))}
			</div>

			{visibleCount < previousWinners.length && (
				<div className="flex w-full flex-row justify-center">
					<Button
						onClick={handleLoadMore}
						variant={"secondary"}
						className="w-50"
					>
						Load More
					</Button>
				</div>
			)}
		</div>
	);
}

export default function WinnersPage() {
	return (
		<div>
			<SEO
				title="Winners - Reel Rewards"
				description="See the Reel Rewards previous winners and the prizes they reeled in. Join the club and you could be next."
			/>
			<Header />
			<PreviousWinners />
		</div>
	);
}
